import React from "react";

import { Island } from "./Island";
import Stack from "./Stack";
import { ToolButton } from "./ToolButton";
import { HandButton } from "./HandButton";
import { t } from "../i18n";
import { isHandToolActive } from "../appState";

import {
  SelectionIcon,
  FreedrawIcon,
  HighlighterIcon,
  LineIcon,
  ArrowIcon,
  DoubleArrowIcon,
  OutlineArrowIcon,
  OutlineDoubleArrowIcon,
  HollowArrowIcon,
  HollowDoubleArrowIcon,
  RectangleIcon,
  EllipseIcon,
  DiamondIcon,
  TriangleIcon,
  PentagonIcon,
  CalloutIcon,
  TextIcon,
  EraserIcon,
} from "./icons";

import type { AppClassProperties, UIAppState } from "../types";
import { TOOL_TYPE } from "@excalidraw/common";

type Props = {
  appState: UIAppState;
  app: AppClassProperties;
  visible?: boolean;
  onHandToolToggle?: () => void;
  showPanActions?: boolean;
};

const LeftMainToolbar: React.FC<Props> = ({
  appState,
  app,
  visible = true,
  onHandToolToggle,
  showPanActions,
}) => {
  if (!visible) {
    return null;
  }
  const active = appState.activeTool.type;
  let lineGroupIcon = LineIcon;
  let shapesGroupIcon = RectangleIcon;
  if (active === TOOL_TYPE.arrow) {
    const start = appState.currentItemStartArrowhead as any;
    const end = appState.currentItemEndArrowhead as any;
    const isDoubleSolid = start === "arrow" && end === "arrow";
    const isSingleSolid = (start == null || start === null) && end === "arrow";
    const isDoubleOutline =
      start === "triangle_outline" && end === "triangle_outline";
    const isSingleOutline =
      (start == null || start === null) && end === "triangle_outline";
    if (isDoubleOutline) {
      lineGroupIcon = OutlineDoubleArrowIcon;
    } else if (isSingleOutline) {
      lineGroupIcon = OutlineArrowIcon;
    } else if (isDoubleSolid) {
      lineGroupIcon = DoubleArrowIcon;
    } else if (isSingleSolid) {
      lineGroupIcon = ArrowIcon;
    } else {
      lineGroupIcon = ArrowIcon;
    }
  } else if (active === TOOL_TYPE.line) {
    lineGroupIcon = LineIcon;
  } else {
    lineGroupIcon = LineIcon;
  }

  // determine shapes group icon based on active shape
  switch (active as any) {
    case TOOL_TYPE.rectangle:
      shapesGroupIcon = RectangleIcon;
      break;
    case TOOL_TYPE.ellipse:
      shapesGroupIcon = EllipseIcon;
      break;
    case TOOL_TYPE.diamond:
      shapesGroupIcon = DiamondIcon;
      break;
    case "triangle":
      shapesGroupIcon = TriangleIcon;
      break;
    case "pentagon":
      shapesGroupIcon = PentagonIcon;
      break;
    case "callout":
      shapesGroupIcon = CalloutIcon;
      break;
    case "hollow_arrow":
      shapesGroupIcon = HollowArrowIcon;
      break;
    case "hollow_double_arrow":
      shapesGroupIcon = HollowDoubleArrowIcon;
      break;
    default:
      // keep last or default rectangle
      break;
  }

  const isHighlighter = (appState.currentItemOpacity ?? 100) < 100;

  const emitFlyoutToggle = (group: "freedraw" | "line" | "shapes" | "text") => {
    try {
      window.dispatchEvent(
        new CustomEvent("leftflyout:toggle", { detail: { group } } as any),
      );
    } catch {}
  };

  return (
    <Island className="App-toolbar App-toolbar--vertical">
      <Stack.Col align="center" gap={1} style={{ padding: "4px 0" }}>
        <ToolButton
          aria-label="selection"
          type="radio"
          icon={SelectionIcon}
          checked={active === TOOL_TYPE.selection}
          name="left-main-toolbar"
          title={t("toolBar.selection")}
          onChange={() => app.setActiveTool({ type: TOOL_TYPE.selection })}
        />
        <HandButton
          checked={isHandToolActive(appState)}
          onChange={() => onHandToolToggle?.()}
          title={t("toolBar.hand")}
          isMobile
          showKeyShortcut={false}
          visible={showPanActions !== false}
        />
        <ToolButton
          aria-label="freedraw"
          type="radio"
          icon={isHighlighter ? HighlighterIcon : FreedrawIcon}
          checked={active === TOOL_TYPE.freedraw}
          name="left-main-toolbar"
          title={t("toolBar.freedraw")}
          onPointerDown={() => emitFlyoutToggle("freedraw")}
          onChange={() => app.setActiveTool({ type: TOOL_TYPE.freedraw })}
        />
        <ToolButton
          aria-label="line"
          type="radio"
          icon={lineGroupIcon}
          checked={active === TOOL_TYPE.line || active === TOOL_TYPE.arrow}
          name="left-main-toolbar"
          title={t("toolBar.line")}
          onPointerDown={() => emitFlyoutToggle("line")}
          onChange={() => app.setActiveTool({ type: TOOL_TYPE.line })}
        />
        <ToolButton
          aria-label="shapes"
          type="radio"
          icon={shapesGroupIcon}
          checked={
            active === TOOL_TYPE.rectangle ||
            active === TOOL_TYPE.ellipse ||
            active === TOOL_TYPE.diamond ||
            (active as any) === "triangle" ||
            (active as any) === "pentagon" ||
            (active as any) === "callout" ||
            (active as any) === "hollow_arrow" ||
            (active as any) === "hollow_double_arrow"
          }
          name="left-main-toolbar"
          title={t("headings.shapes")}
          onPointerDown={() => emitFlyoutToggle("shapes")}
          onChange={() => app.setActiveTool({ type: TOOL_TYPE.rectangle })}
        />
        <ToolButton
          aria-label="text"
          type="radio"
          icon={TextIcon}
          checked={active === TOOL_TYPE.text}
          name="left-main-toolbar"
          title={t("toolBar.text")}
          onPointerDown={() => emitFlyoutToggle("text")}
          onChange={() => app.setActiveTool({ type: TOOL_TYPE.text })}
        />
        <ToolButton
          aria-label="eraser"
          type="radio"
          icon={EraserIcon}
          checked={active === TOOL_TYPE.eraser}
          name="left-main-toolbar"
          title={t("toolBar.eraser")}
          onChange={() => app.setActiveTool({ type: TOOL_TYPE.eraser })}
        />
      </Stack.Col>
    </Island>
  );
};

export default LeftMainToolbar;
